import React from 'react';
import {
  Badge,
  Button,
  VStack,
  Text,
  HStack,
  Icon,
} from '@chakra-ui/react';
import { ClipboardList, List, Edit } from 'lucide-react';
import Drawer, { DrawerHeader, DrawerTitle, DrawerBody, DrawerFooter, DrawerCloseTrigger } from '@/components/ui/Drawer';
import DetailsSection from '@/components/ui/DetailsSection';
import LoadingSkeleton from '@/components/LoadingSkeleton';
import RelativeTime from '@/components/ui/RelativeTime';
import withHashDrawer from '@/components/ui/withHashDrawer';
import { BaseDetailsModalProps } from '@/types/DetailsModalTypes';
import { useGetConditionGroupConditionsQuery, useGetConditionGroupLookupQuery } from '../api';
import { ConditionLookup } from '@entities/conditions/api';
import { getStatusBadgeProps } from '@/utils/lookupStatus';

interface ConditionGroupDetailsModalProps extends BaseDetailsModalProps {
  // No additional props needed for ConditionGroupDetailsModal
}

const ConditionGroupDetailsModal: React.FC<ConditionGroupDetailsModalProps> = ({
  isOpen,
  onClose,
  navigateToModal,
  entityId,
}) => {
  // Fetch individual condition group
  const { 
    data: selectedGroup, 
    isLoading: isLoadingGroup, 
    error: groupError 
  } = useGetConditionGroupLookupQuery(entityId!, {
    skip: !entityId
  });

  // Fetch conditions for this group
  const { data: conditions = [], isLoading: isLoadingConditions } = useGetConditionGroupConditionsQuery(
    entityId || 0, 
    { skip: !entityId }
  );

  const handleEditGroup = () => {
    if (selectedGroup && navigateToModal) {
      navigateToModal(selectedGroup.id, 'edit');
    }
  };

  const handleEditConditions = () => {
    if (selectedGroup && navigateToModal) {
      navigateToModal(selectedGroup.id, 'conditions');
    }
  };
  const renderContent = () => {
    if (isLoadingGroup || isLoadingConditions) {
      return <LoadingSkeleton />;
    }

    if (groupError) {
      return <Text color="red.500">Error loading condition group details</Text>;
    }

    if (!selectedGroup) {
      return <Text>Condition group not found</Text>;
    }

    return (
      <VStack gap={6} align="stretch">
        {/* Basic Information */}
        <DetailsSection
          title="Basic Information"
          icon={ClipboardList}
          items={[
            {
              label: "ID",
              value: selectedGroup.id
            },
            {
              label: "Name",
              value: selectedGroup.name
            },
            {
              label: "Description",
              value: selectedGroup.description,
              stacked: true,
              render: () => selectedGroup.description ? (
                <Text fontSize="sm">{selectedGroup.description}</Text>
              ) : (
                <Text fontSize="sm" color="gray.500">No description provided</Text>
              )
            },
            {
              label: "Status",
              value: selectedGroup.status,
              render: () => {
                const { colorPalette, label } = getStatusBadgeProps(selectedGroup.status);
                return (
                  <Badge colorPalette={colorPalette} size="sm" variant="subtle">
                    {label}
                  </Badge>
                );
              }
            },
            {
              label: "Total Conditions",
              value: conditions?.length || 0
            },
            {
              label: "Created",
              value: selectedGroup.created_at,
              render: () => <RelativeTime date={selectedGroup.created_at} />
            },
            {
              label: "Updated",
              value: selectedGroup.updated_at,
              render: () => <RelativeTime date={selectedGroup.updated_at} />
            }
          ]}
        />

        {/* Assigned Conditions */}
        <DetailsSection
          title="Assigned Conditions"
          icon={List}
          items={
            conditions && conditions.length > 0
              ? conditions.map((condition: ConditionLookup, index: number) => ({
                  label: `${index + 1}.`,
                  value: condition.id,
                  render: () => (
                    <VStack align="start" gap={1} w="full">
                      <Text fontWeight="medium" fontSize="sm">
                        {condition.name}
                      </Text>
                      <Text fontSize="sm" color="gray.600" _dark={{ color: "gray.400" }} textAlign="left">
                        {condition.description}
                      </Text>
                    </VStack>
                  )
                }))
              : [
                  {
                    label: "No conditions",
                    value: "empty",
                    render: () => (
                      <Text color="gray.500" fontSize="sm">
                        No conditions assigned to this group
                      </Text>
                    )
                  }
                ]
          }
        />
      </VStack>
    );
  };

  return (
    <Drawer isOpen={isOpen} onClose={onClose} size="lg">
      <DrawerHeader>
        <DrawerTitle color="brand.700" _dark={{ color: "brand.300" }}>
          Condition Group Details {selectedGroup ? `- ${selectedGroup.name}` : ''}
        </DrawerTitle>
        <DrawerCloseTrigger />
      </DrawerHeader>
      
      <DrawerBody>
        {renderContent()}
      </DrawerBody>
      
      <DrawerFooter>
        <HStack justify="space-between" w="full">
          <HStack>
            {selectedGroup && (
              <>
                <Button variant="outline" onClick={handleEditGroup}>
                  <Icon as={Edit} boxSize={4} mr={2} />
                  Edit Group
                </Button>
                <Button variant="outline" onClick={handleEditConditions}>
                  <Icon as={Edit} boxSize={4} mr={2} />
                  Edit Conditions
                </Button>
              </>
            )}
          </HStack>
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </HStack>
      </DrawerFooter>
    </Drawer>
  );
};

// Wrap with HOC for hash-based rendering (details modal: view action)
export default withHashDrawer(ConditionGroupDetailsModal, { 
  action: 'view' 
}); 
